/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-case-declarations */
import {
    iventisAreaToAggregateLayer,
    iventisIconToAggregateLayer,
    iventisLineToAggregateLayer,
    iventisPointToAggregateLayer,
} from "@iventis/map-engine/src/bridge/mapbox/engine-mapbox-helpers";
import { LayerStorageScope, MapModuleLayer } from "@iventis/map-engine";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { AreaStyle } from "@iventis/domain-model/model/areaStyle";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { LineStyle } from "@iventis/domain-model/model/lineStyle";
import { PointStyle } from "@iventis/domain-model/model/pointStyle";
import { IconStyle } from "@iventis/domain-model/model/iconStyle";
import { ExpressionSpecification, Map } from "@iventis/mapbox-gl";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { IventisExportedMapLayerDataFieldListItem } from "@iventis/domain-model/model/iventisExportedMapLayerDataFieldListItem";
import {
    AreaMapImportLayer,
    IconMapImportLayer,
    LineMapImportLayer,
    LineModelMapImportLayer,
    MapImportLayer,
    ModelMapImportLayer,
    PointMapImportLayer,
} from "../types/export-types";
import { IventisLayer } from "../types/layer-types";

/** Given an export layer get the mapbox layer(s) to represent it */
export function getMapboxLayer(exportLayer: MapImportLayer, map: Map) {
    const baseLayer = createIventisBaseLayer(exportLayer);

    if (baseLayer == null) {
        throw new Error("Can't create base layer");
    }

    const applyTextFieldExpression = (expression: ExpressionSpecification, mapboxTextLayerId: string) => {
        map.setLayoutProperty(mapboxTextLayerId, "text-field", expression);
        return Promise.resolve();
    };

    const getAttrbuteListItems = (attributeId: string): Promise<DataFieldListItem[]> => {
        const dataField = exportLayer.dataFields.find((df) => df.id === attributeId);
        const dataFieldListItems = dataField.listItems.map(parseIventisExportedMapLayerDataFieldToListItemToDataFieldListItem);
        return Promise.resolve(dataFieldListItems);
    };

    switch (exportLayer.styleType) {
        case StyleType.Area:
            const areaLayer = createIventisAreaLayer(exportLayer, baseLayer);
            return iventisAreaToAggregateLayer(
                areaLayer as MapModuleLayer,
                false,
                { projectDataFields: [], unitOfMeasurement: UnitOfMeasurement.Metric },
                ["==", "level", 0],
                applyTextFieldExpression,
                getAttrbuteListItems
            );
        case StyleType.Line:
            const lineLayer = createIventisLineLayer(exportLayer, baseLayer);
            return iventisLineToAggregateLayer(
                lineLayer as MapModuleLayer,
                false,
                { projectDataFields: [], unitOfMeasurement: UnitOfMeasurement.Metric },
                ["==", "level", 0],
                applyTextFieldExpression,
                getAttrbuteListItems
            );
        case StyleType.Point:
            const pointLayer = createIventisPointLayer(exportLayer, baseLayer);
            return iventisPointToAggregateLayer(
                pointLayer as MapModuleLayer,
                false,
                { projectDataFields: [], unitOfMeasurement: UnitOfMeasurement.Metric },
                ["==", "level", 0],
                applyTextFieldExpression,
                getAttrbuteListItems
            );
        case StyleType.Icon:
            const iconLayer = createIventisIconLayer(exportLayer, baseLayer);
            return iventisIconToAggregateLayer(
                iconLayer as MapModuleLayer,
                false,
                {},
                { projectDataFields: [], unitOfMeasurement: UnitOfMeasurement.Metric },
                ["==", "level", 0],
                applyTextFieldExpression,
                getAttrbuteListItems
            );
        default:
            throw new Error("Unknown style type");
    }
}

/** Given an export layer return an iventis layer, deckgl uses Iventis layer not a mapbox layer */
export function createModelLayer(exportLayer: MapImportLayer): IventisLayer {
    switch (exportLayer.styleType) {
        case StyleType.Model:
            return createIventisModelLayer(exportLayer);
        case StyleType.LineModel:
            return createIventisLineModelLayer(exportLayer);
        default:
            throw new Error("Unknown style type");
    }
}

function createIventisAreaLayer(exportLayer: AreaMapImportLayer, baseLayer: IventisLayer): IventisLayer {
    return {
        ...baseLayer,
        areaStyle: exportLayer.style as AreaStyle,
    };
}

function createIventisLineLayer(exportLayer: LineMapImportLayer, baseLayer: IventisLayer): IventisLayer {
    return {
        ...baseLayer,
        lineStyle: exportLayer.style as LineStyle,
    };
}

function createIventisPointLayer(exportLayer: PointMapImportLayer, baseLayer: IventisLayer): IventisLayer {
    return {
        ...baseLayer,
        pointStyle: exportLayer.style as PointStyle,
    };
}

function createIventisIconLayer(exportLayer: IconMapImportLayer, baseLayer: IventisLayer): IventisLayer {
    return {
        ...baseLayer,
        iconStyle: exportLayer.style as IconStyle,
    };
}

function createIventisModelLayer(exportLayer: ModelMapImportLayer): IventisLayer {
    return {
        ...createIventisBaseLayer(exportLayer),
        modelStyle: exportLayer.style,
    };
}

function createIventisLineModelLayer(exportLayer: LineModelMapImportLayer): IventisLayer {
    return {
        ...createIventisBaseLayer(exportLayer),
        lineModelStyle: exportLayer.style,
    };
}

function parseIventisExportedMapLayerDataFieldToListItemToDataFieldListItem(exportedListItem: IventisExportedMapLayerDataFieldListItem): DataFieldListItem {
    return {
        customerIdentifier: "",
        name: exportedListItem.name,
        metaData: undefined,
        order: exportedListItem.order,
        propertyValues: exportedListItem.propertyValues,
        relationshipValues: exportedListItem.relationshipValues,
        uniqueId: exportedListItem.uniqueId,
        dataFieldId: exportedListItem.dataFieldId,
        id: exportedListItem.id,
    };
}

/** Creates a base iventis layer from an export layer */
function createIventisBaseLayer(exportLayer: MapImportLayer): IventisLayer {
    return {
        source: exportLayer.id,
        stamp: "",
        selected: false,
        storageScope: LayerStorageScope.LocalOnly,
        id: exportLayer.id,
        name: exportLayer.name,
        visible: exportLayer.visible,
        styleType: exportLayer.styleType,
        areaStyle: undefined,
        lineStyle: undefined,
        pointStyle: undefined,
        iconStyle: undefined,
        modelStyle: undefined,
        lineModelStyle: undefined,
        areaModelStyle: undefined,
        dataFields: [],
        locked: false,
    };
}
